<script lang="ts">
  import type * as t from "./types";
  import { type SequenceNode } from "./sequence";

  import { onMount } from "svelte";
  import { derived, get, type Readable } from "svelte/store";
  import { fade } from "svelte/transition";
  import ActionButton from "./components/ActionButton.svelte";
  import PaddingX from "./components/PaddingX.svelte";
  import ShopifyCdnImage from "./components/ShopifyCdnImage.svelte";
  import StepHeader from "./components/StepHeader.svelte";
  import { getAppContext } from "./quiz-store";
  import { Err, Ok, overrideSegmentPrize, tryCatch } from "./utils";

  export let stepNode: SequenceNode<t.Step>;
  export let visible: boolean;

  const STATUSES = [
    "initial",
    "loading",
    "ready",
    "playing",
    "finished",
  ] as const;
  const STATUS_EVENT_MAP: PartialRecord<MinigameStatus, string> = {
    playing: "minigame_start",
    finished: "minigame_end",
  };
  const MANAGED_EVENTS = ["minigame_start", "minigame_end"];
  const VALID_STATUS_TRANSITIONS: Record<
    MinigameStatus,
    Array<MinigameStatus>
  > = {
    initial: ["loading"],
    loading: ["ready"],
    ready: ["playing"],
    playing: ["finished"],
    finished: [],
  };
  const STATUS_TRANSITION_ACTIONS: PartialRecord<
    MinigameStatus,
    PartialRecord<MinigameStatus, MinigameStatusTransitionAction>
  > = {
    initial: {
      loading: () => {
        logMinigameEvent("minigame_load_start");
      },
    },
    loading: {
      ready: () => {
        logMinigameEvent("minigame_load_end");
      },
    },
  };
  const STATUS_UPDATE_ACTIONS: PartialRecord<
    MinigameStatus,
    MinigameStatusTransitionAction
  > = {
    finished: handleFinishMinigame,
  };

  type PartialRecord<K extends keyof any, T> = {
    [P in K]?: T;
  };
  type MinigameStatus = (typeof STATUSES)[number];
  type MinigameMessage = {
    event: string;
    instanceId: string;
    data: unknown;
  };
  type MinigameSetupMessage = MinigameMessage & {
    data: { name: string; version: string };
  };
  type MinigameStatusUpdateMessage = MinigameMessage & {
    data: { status: MinigameStatus };
  };
  type MinigameResultMessage = MinigameMessage & {
    data: {
      result: t.MinigameResult;
    };
  };
  type MinigameTelemetryMessage = MinigameMessage & {
    data: {
      action: string;
      properties?: Record<string, any>;
    };
  };

  type MinigameConfig = {
    name: string;
    version: string;
  };
  type MinigameStatusTransitionAction = () => void;

  let minigameConfig: MinigameConfig | undefined;
  let internalStep: "initial" | "minigame" = "initial";
  // internalGameStatus is defined only after Start button is clicked.
  let internalGameStatus: MinigameStatus | undefined;
  let result: t.MinigameResult | undefined;

  const ctx = getAppContext();
  const instanceId = get(ctx.state).instanceId;
  const gameStatus: Readable<MinigameStatus> = derived(ctx.state, ($) => {
    if ($.step.value.type !== "construct_minigame") {
      return "initial";
    }
    return $.step.value.state.gameStatus;
  });
  const state = ctx.state;
  const step = (stepNode as SequenceNode<t.ConstructMinigameStep>).value;
  let title: string = step.title;
  let iframeSrc: string = step.iframeSrc;

  state.subscribe(($) => {
    const updatedStep = $.step as SequenceNode<t.ConstructMinigameStep>;
    if (updatedStep.id !== stepNode.id) {
      return;
    }

    if (updatedStep.value.state.title != null) {
      title = updatedStep.value.state.title;
    }

    if (updatedStep.value.state.iframeSrc) {
      iframeSrc = updatedStep.value.state.iframeSrc;
    }
  });

  function handleFinishMinigame() {
    ctx.actions.goToNextStep();
  }

  function validateStatusTransition(
    prevStatus: MinigameStatus,
    newStatus: MinigameStatus,
  ) {
    if (!VALID_STATUS_TRANSITIONS[prevStatus]?.includes(newStatus)) {
      console.error(
        `Unexpected minigame status transition: '${prevStatus}' > '${newStatus}'`,
      );
    }
  }

  function handleSetup(msg: MinigameSetupMessage) {
    minigameConfig = {
      name: msg.data.name,
      version: msg.data.version,
    };
  }

  function handleStatusUpdate(msg: MinigameStatusUpdateMessage) {
    const prevStatus = step.state.gameStatus;
    const { status } = msg.data;

    if (!STATUSES.includes(status)) {
      throw new Error(`Invalid minigame status: '${status}'`);
    }

    // Handle side effects
    switch (status) {
      case "initial":
        // Ignore initial status update as this is the default state.
        return;
      case "playing":
        // Reset result in case it's a try-again situation in the game.
        result = undefined;
    }

    ctx.state.update(($) => {
      if ($.step.value.type !== "construct_minigame") {
        return $;
      }
      $.step.value.state.gameStatus = status;
      return $;
    });

    if (minigameConfig == null) {
      throw new Error("`minigameConfig` is unexpectedly null.");
    }

    executeStatusTransitionAction(prevStatus, status);
    executeStatusUpdateAction(status);

    const event = STATUS_EVENT_MAP[status];
    if (event == null) {
      return;
    }

    logMinigameEvent(event);
  }

  /*
   * Executes an action for a status regardless of the status that came before it.
   * */
  function executeStatusUpdateAction(newStatus: MinigameStatus) {
    const action = STATUS_UPDATE_ACTIONS[newStatus];
    action?.();
  }

  /*
   * Executes an action defined for a specific status transition.
   * */
  function executeStatusTransitionAction(
    prevStatus: MinigameStatus,
    newStatus: MinigameStatus,
  ) {
    validateStatusTransition(prevStatus, newStatus);

    const actions = STATUS_TRANSITION_ACTIONS[prevStatus];
    if (actions == null) {
      return;
    }

    const action = actions[newStatus];
    action?.();
  }

  function handleResult(msg: MinigameResultMessage) {
    result = msg.data.result;
  }

  function handleTelemetry(msg: MinigameTelemetryMessage) {
    // If the games posts a telemetry message with one of the manages events,
    // we don't log it because that would cause duplicate events.
    if (msg.data.action in MANAGED_EVENTS) {
      return;
    }
    logMinigameEvent(msg.data.action, msg.data.properties);
  }

  function logMinigameEvent(action: string, properties?: Record<string, any>) {
    if (minigameConfig == null) {
      throw new Error("`minigameConfig` is unexpectedly null.");
    }

    ctx.telemetry.logEvent(action, {
      name: minigameConfig.name,
      version: minigameConfig.version,
      ...(properties ?? {}),
    });
  }

  function parseOriginUrl(e: MessageEvent<MinigameMessage>) {
    try {
      const url = new URL(e.origin);
      return Ok(url);
    } catch (err) {
      return Err<Error>(err as Error);
    }
  }

  function handleWindowMessage(e: MessageEvent<MinigameMessage>) {
    const srcUrl = new URL(iframeSrc);
    const [originUrl, err] = parseOriginUrl(e);
    if (err != null) {
      console.error("[shpln-quiz] Error parsing minigame origin URL", err);
      return;
    }
    if (originUrl.origin !== srcUrl.origin) {
      return;
    }

    const msg = e.data;

    if (msg.instanceId !== instanceId) {
      return;
    }

    switch (msg.event) {
      case "setup":
        handleSetup(msg as MinigameSetupMessage);
        break;
      case "status_update":
        handleStatusUpdate(msg as MinigameStatusUpdateMessage);
        break;
      case "telemetry":
        handleTelemetry(msg as MinigameTelemetryMessage);
        break;
      case "result":
        handleResult(msg as MinigameResultMessage);
    }
  }

  function getResultTypeMismatchError(res: t.MinigameResult): Error {
    return new Error(
      `Minigame result type mismatch: '${res.type}' and '${step.config.type}'.`,
    );
  }

  function isMinigameResultScore(
    res: t.MinigameResult,
  ): res is t.MinigameResultScore {
    return res.type === "score";
  }
  function isMinigameResultWinLose(
    res: t.MinigameResult,
  ): res is t.MinigameResultWinLose {
    return res.type === "win_lose";
  }
  function isMinigameResultCustom(
    res: t.MinigameResult,
  ): res is t.MinigameResultCustom {
    return res.type === "custom";
  }

  function isMinigameConfigScore(
    config: t.ConstructMinigameConfig,
  ): config is t.MinigameConfigScore {
    return config.type === "score";
  }
  function isMinigameConfigWinLose(
    config: t.ConstructMinigameConfig,
  ): config is t.MinigameConfigWinLose {
    return config.type === "win_lose";
  }
  function isMinigameConfigCustom(
    config: t.ConstructMinigameConfig,
  ): config is t.MinigameConfigCustom {
    return config.type === "custom";
  }

  function computeScoreResult(res: t.MinigameResultScore): t.QuizResult {
    if (!isMinigameResultScore(res) || !isMinigameConfigScore(step.config)) {
      throw getResultTypeMismatchError(res);
    }

    let range: t.MinigameConfigScore["prize"]["ranges"][number] | undefined;
    loop: for (const r of step.config.prize.ranges) {
      if (r.min == null && r.max == null) {
        throw new Error("Both min and max values of range are undefined");
      }

      switch (true) {
        case r.min == null && res.value <= r.max!:
          range = r;
          break loop;
        case r.max == null && res.value >= r.min!:
          range = r;
          break loop;
        case r.min! <= res.value && res.value <= r.max!:
          range = r;
          break loop;
      }
    }

    if (range == null) {
      throw new Error(`No prize found for score: ${res.value}`);
    }

    return {
      discountCode: range.discount_code,
      redirectUrl: range.redirect_url,
    };
  }

  function computeWinLoseResult(res: t.MinigameResultWinLose): t.QuizResult {
    if (
      !isMinigameResultWinLose(res) ||
      !isMinigameConfigWinLose(step.config)
    ) {
      throw getResultTypeMismatchError(res);
    }

    if (res.value !== "win" && res.value !== "lose") {
      throw new Error(
        `Invalid result type '${res.value}' for minigame with win_lose result type.`,
      );
    }

    const configRes = step.config.prize[res.value];
    return {
      discountCode: configRes.discount_code,
      redirectUrl: configRes.redirect_url,
    };
  }

  function computeCustomResult(res: t.MinigameResultCustom): t.QuizResult {
    if (!isMinigameResultCustom(res) || !isMinigameConfigCustom(step.config)) {
      throw getResultTypeMismatchError(res);
    }

    const prize = step.config.prize[res.value];

    return {
      discountCode: prize.discount_code,
      redirectUrl: prize.redirect_url,
    };
  }

  function computeResult(res: t.MinigameResult): t.QuizResult {
    switch (res.type) {
      case "score":
        return computeScoreResult(res as t.MinigameResultScore);
      case "win_lose":
        return computeWinLoseResult(res as t.MinigameResultWinLose);
      case "custom":
        return computeCustomResult(res as t.MinigameResultCustom);
      default:
        throw new Error(`Unknown minigame result type: '${res.type}'`);
    }
  }

  function getCustomPrize(res: t.MinigameResult): t.CustomPrize {
    if (!isMinigameResultCustom(res) || !isMinigameConfigCustom(step.config)) {
      throw getResultTypeMismatchError(res);
    }

    const prize = step.config.prize[res.value];

    return {
      type: "custom",
      custom_value: prize.title,
    };
  }

  onMount(() => {
    window.addEventListener("message", handleWindowMessage);

    ctx.hooks.onBeforeProceed(stepNode, (state) => {
      if (result == null) {
        return;
      }

      let quizResult: t.QuizResult;
      try {
        quizResult = computeResult(result);
      } catch (err) {
        console.error(`[shpln-quiz] Error calculating minigame result`, err);
        return;
      }

      state.update(($) => {
        if (result == null) {
          return $;
        }

        $.minigameResult = quizResult;

        // When minigame result is "custom", we need to override the
        // segment's prize so the next screen can display the correct
        // prize title.
        if (result.type === "custom") {
          const prize = getCustomPrize(result);
          overrideSegmentPrize($, $.segment, prize);
        }

        return $;
      });
    });

    return () => {
      window.removeEventListener("message", handleWindowMessage);
    };
  });

  function handleClickStart() {
    const status = get(gameStatus);
    if (status === "ready") {
      internalStep = "minigame";
      return;
    }

    const unsubscribe = gameStatus.subscribe((status) => {
      internalGameStatus = status;
      if (status === "ready") {
        internalStep = "minigame";
        unsubscribe();
      }
    });
  }
</script>

{#if visible}
  <div class="relative size-full flex-1" data-internal-step={internalStep}>
    <div class="absolute inset-0">
      <PaddingX class="size-full">
        <iframe
          src={`${iframeSrc}?instanceId=${instanceId}`}
          title="PlayAbly minigame"
          class="size-full transition-opacity"
        ></iframe>
      </PaddingX>
    </div>
    {#if internalStep === "initial"}
      <div
        class="absolute inset-0 flex flex-col bg-white"
        transition:fade|local
      >
        <StepHeader {title} subtitle={step.subtitle} />
        <PaddingX class="flex-1">
          <div
            class="mt-4 flex size-full flex-col items-center justify-center gap-4"
          >
            {#if step.image != null}
              <div class="relative h-full max-h-[50%] w-full">
                <ShopifyCdnImage
                  image={step.image}
                  style="--image-object-fit: {step.imageObjectFit};"
                  class="image absolute inset-0 size-full"
                />
              </div>
            {/if}
            {#if step.imageCaption != null}
              <span>{step.imageCaption}</span>
            {/if}
            <ActionButton
              on:click={handleClickStart}
              buttonStyle="gradient-outline"
              networkStatus={internalGameStatus != null ? "loading" : "idle"}
              class="mt-4"
            >
              <span slot="title">
                {step.buttonText}
              </span>
            </ActionButton>
          </div>
        </PaddingX>
      </div>
    {/if}
  </div>
{/if}

<style>
  .image {
    object-fit: var(--image-object-fit, contain);
  }
</style>
